import { Paper, LinearProgress } from '@mui/material';
import { useLocation } from 'react-router';
import type { IMATEntry, MatDiscoveryResponse } from '../../types/mat-types';
import { MAT_FILTER_KEYS, MAT_STATUS_OPTIONS } from '../../types/mat-types';
import useGlynt from '../hooks/useGlynt';
import { AppContext } from '../../App';
import MATGridContextMenu from './MATGridContextMenu';
import MATExpandIconCell from './MATExpandIconCell';
import MATRowDetailDrawer from './MATRowDetailDrawer';
import { applyClientSideFilters } from './MATGrid.utils';
import {
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useLayoutEffect,
  useContext,
  memo,
  type FC,
} from 'react';
import useMatUrlFilters from './hooks/useMatUrlFilters';
import { AgGridReact } from 'ag-grid-react';
import useMatDataSource from './hooks/useMatDataSource';
import type { GridApi, ColDef, CellContextMenuEvent } from 'ag-grid-community';
import { useQuery } from '@tanstack/react-query';
import { transformDiscoveredData } from './hooks/useMatDataSource.utils';
import type { TransformedMATRow } from './hooks/useMatDataSource.types';
import type { MATGridProps, MenuState, GetRowClassParams } from './MATGrid.types';
import {
  fetchAllDiscoveredData,
  createExpandColumnDef,
  createVendorTicketColumnDef,
  getRowClasses,
} from './MATGrid.utils.helpers';
import MATVendorTicketCell from './MATVendorTicketCell';
import useMatDiscoveryKeys from './useMatDiscoveryKeys';
import useMatCFieldKeys from './hooks/useMatCFieldKeys';

const MAT_GRID_EXPAND_COLUMN_FIELD = '__expand__';
const MAT_GRID_VENDOR_TICKET_COLUMN_FIELD = '__vendorTicket__';

/**
 * Infinite row model tuning. Data pools reach ~6k MAT entries, so the cache has to
 * hold enough blocks that scrolling back over ground already covered does not evict
 * and refetch it, while the debounce keeps a fast scroll from queuing a request for
 * every block it flies past.
 */
const MAT_GRID_CACHE_BLOCK_SIZE = 100;
const MAT_GRID_MAX_BLOCKS_IN_CACHE = 20;
const MAT_GRID_BLOCK_LOAD_DEBOUNCE_MS = 150;
const MAT_GRID_MAX_CONCURRENT_REQUESTS = 2;
const MAT_GRID_ROW_BUFFER = 5;

/** Only these fields are filterable (typed, shared). */
const SERVER_FILTER_FIELDS: ReadonlySet<string> = new Set<string>(
  MAT_FILTER_KEYS as readonly string[]
);

const MATGrid: FC<MATGridProps> = ({
  onTotalCountChange,
  onMissingClientChange,
  includeMatData,
  includeDiscovered,
  includeReviewedOnly,
  vendorExistsFilter,
  onRefreshNeeded,
}) => {
  const { orgId, dataPoolId } = useContext(AppContext);

  const glynt = useGlynt();
  const { search } = useLocation();

  // Use refs to store current checkbox values so datasource can always read the latest
  const includeMatDataRef = useRef(includeMatData);
  const includeDiscoveredRef = useRef(includeDiscovered);
  const includeReviewedOnlyRef = useRef(includeReviewedOnly);
  const vendorExistsFilterRef = useRef(vendorExistsFilter);
  const dataRequestVersionRef = useRef(0);

  // Update refs when props change
  // Use useLayoutEffect to ensure refs are updated synchronously before any effects
  // that might trigger cache purges (which call getRows and read these refs)
  useLayoutEffect(() => {
    dataRequestVersionRef.current += 1;
    includeMatDataRef.current = includeMatData;
    includeDiscoveredRef.current = includeDiscovered;
    includeReviewedOnlyRef.current = includeReviewedOnly;
    vendorExistsFilterRef.current = vendorExistsFilter;
  }, [includeMatData, includeDiscovered, includeReviewedOnly, vendorExistsFilter]);

  // Fetch C-Field keys from first page of existing MAT entries
  const cFieldKeys = useMatCFieldKeys();

  // Fetch discovered mat keys for highlighting
  const { data: discoveryKeysData } = useMatDiscoveryKeys({ params: { has_existing_mat: 'true' } });

  const allMatKeys = useMemo(() => {
    return new Set(discoveryKeysData?.discovered_mat_keys || []);
  }, [discoveryKeysData?.discovered_mat_keys]);

  // Fetch ALL discovered data for the grid (when includeDiscovered is true)
  // Makes multiple paginated API calls to get everything since filtering isn't supported server-side
  const { data: fullDiscoveryData, isPending: isLoadingDiscovery } = useQuery({
    queryKey: ['mat-discovery-full', dataPoolId, includeReviewedOnly],

    queryFn: async () => {
      const g = await glynt;
      return fetchAllDiscoveredData(
        (params) => g.mat.matDiscovery(params) as Promise<MatDiscoveryResponse>,
        includeReviewedOnly
      );
    },

    enabled: !!dataPoolId && includeDiscovered,

    // 5 minutes
    staleTime: 5 * 60_000,

    // 10 minutes
    gcTime: 10 * 60_000,
  });

  // Transform and filter discovered data (cached to avoid re-computation on every pagination request)
  const transformedDiscoveredRows = useMemo<TransformedMATRow[]>(() => {
    if (!fullDiscoveryData?.results) return [];

    // Transform the data
    const transformed = transformDiscoveredData(fullDiscoveryData.results);

    // Apply client-side filtering since the endpoint doesn't support filtering
    const urlSp = new URLSearchParams(search);
    let filtered = applyClientSideFilters(transformed, urlSp, SERVER_FILTER_FIELDS);

    // Apply vendor exists filter
    if (vendorExistsFilter === 'true') {
      // Show only rows where std-VendorName has a value
      filtered = filtered.filter((row) => {
        const stdVendorName = row['std-VendorName'];
        return stdVendorName != null && stdVendorName !== '';
      });
    } else if (vendorExistsFilter === 'false') {
      // Show only rows where std-VendorName is empty/null
      filtered = filtered.filter((row) => {
        const stdVendorName = row['std-VendorName'];
        return stdVendorName == null || stdVendorName === '';
      });
    }

    return filtered;
  }, [fullDiscoveryData, search, vendorExistsFilter]);

  // Store transformed discovered data in a ref for datasource access
  // Use useLayoutEffect to ensure ref is updated BEFORE cache purge effects run
  const transformedDiscoveredRowsRef = useRef<TransformedMATRow[]>([]);
  useLayoutEffect(() => {
    dataRequestVersionRef.current += 1;
    transformedDiscoveredRowsRef.current = transformedDiscoveredRows;
  }, [transformedDiscoveredRows]);

  // Store current search params in a ref to ensure datasource uses the same filters
  // that were used to filter discovered rows (prevents count mismatches)
  const currentSearchRef = useRef<string>(search);
  useLayoutEffect(() => {
    dataRequestVersionRef.current += 1;
    currentSearchRef.current = search;
  }, [search]);

  // Check if any discovered rows have missing client (ClientName and ClientAbbr both null/empty)
  const hasDiscoveredRowsMissingClient = useMemo(() => {
    if (!includeDiscovered || transformedDiscoveredRows.length === 0) return false;
    return transformedDiscoveredRows.some((row) => {
      const clientName = row.ClientName;
      const clientAbbr = row.ClientAbbr;
      return (clientName == null || clientName === '') && (clientAbbr == null || clientAbbr === '');
    });
  }, [includeDiscovered, transformedDiscoveredRows]);

  // Notify parent when missing client state changes
  useEffect(() => {
    onMissingClientChange?.(hasDiscoveredRowsMissingClient);
  }, [hasDiscoveredRowsMissingClient, onMissingClientChange]);

  const gridRef = useRef<AgGridReact<IMATEntry> | null>(null);
  const gridApiRef = useRef<GridApi | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Combined loading state includes React Query loading for discovered data
  const isPending = useMemo(
    () => loading || (includeDiscovered && isLoadingDiscovery),
    [loading, includeDiscovered, isLoadingDiscovery]
  );

  const [menuState, setMenuState] = useState<MenuState>({
    mouseX: null,
    mouseY: null,
    cellRaw: undefined,
    columnField: undefined,
  });

  const closeMenu = useCallback(() => {
    setMenuState({ mouseX: null, mouseY: null, cellRaw: undefined, columnField: undefined });
  }, []);

  const handleContextMenuFilter = useCallback((columnField: string, value: string) => {
    const gridApi = gridApiRef.current;
    if (!gridApi) return;

    // Get current filter model
    const currentModel = gridApi.getFilterModel?.() || {};

    // Update with new filter
    const newModel = {
      ...currentModel,
      [columnField]: { filterType: 'text', type: 'contains', filter: value },
    };

    // Apply the filter model to the grid
    gridApi.setFilterModel?.(newModel);
  }, []);

  // Build column defs after first server fetch to preserve order
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const columnDefsInitialized = useRef(false);
  const dataSourceInitialized = useRef(false);

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 120,
      sortable: true,
      filter: true,
      floatingFilter: true,
      // Lock filters to single condition and only 'contains'
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        suppressAndOrCondition: true,
        maxNumConditions: 1,
        debounceMs: 300,
      },
    }),
    []
  );

  // Drawer state for row details
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<IMATEntry | null>(null);
  const selectedRowDataRef = useRef<IMATEntry | null>(null);

  const handleExpandRow = useCallback((data: IMATEntry) => {
    selectedRowDataRef.current = data;
    setSelectedRowData(data);
    setDrawerOpen(true);
    // Redraw rows to apply row highlight
    setTimeout(() => gridApiRef.current?.redrawRows(), 0);
  }, []);

  const handleDrawerSuccess = useCallback(() => {
    // Purge AG Grid cache to force refresh with updated data
    gridApiRef.current?.purgeInfiniteCache?.();
  }, []);

  const handleCloseDrawer = useCallback(() => {
    selectedRowDataRef.current = null;
    setDrawerOpen(false);
    setSelectedRowData(null);
    // Redraw rows to remove highlight
    setTimeout(() => gridApiRef.current?.redrawRows(), 0);
  }, []);

  // Minimal: build next URL from grid filter model and compare strings
  const { applyingUrlFiltersRef, setUrlFromFilterModel, urlToFilterModel, modelsEqual } =
    useMatUrlFilters();

  // using hook for URL<->grid synchronization

  // Datasource for Infinite Row Model
  const setupDataSource = useMatDataSource({
    glyntPromise: glynt,
    orgId,
    onTotalCountChange,
    setLoading,
    setError,
    setColumnDefs,
    columnDefsInitialized,
    dataSourceInitialized,
    serverFilterFields: SERVER_FILTER_FIELDS,
    includeMatDataRef,
    includeDiscoveredRef,
    transformedDiscoveredRowsRef,
    currentSearchRef,
    dataRequestVersionRef,
  });

  // Add pinned left columns (expand icon + vendor ticket icon) after columns are initialized
  // Also add cellStyle to VendorName column to highlight missing std-VendorName on discovered rows
  useEffect(() => {
    if (columnDefs.length > 0 && columnDefs[0].field !== MAT_GRID_EXPAND_COLUMN_FIELD) {
      const expandColumn = createExpandColumnDef(
        handleExpandRow,
        (data) => selectedRowDataRef.current?.MatKey === data?.MatKey,
        MATExpandIconCell
      );
      const vendorTicketColumn = createVendorTicketColumnDef(MATVendorTicketCell);
      const enhanced = columnDefs.map((col) => {
        if (col.field === 'VendorName') {
          return {
            ...col,
            cellStyle: (params: any) => {
              const isDiscovered = !!params.data?.__isDiscovered__;
              const stdVendor = params.data?.['std-VendorName'];
              if (isDiscovered && (stdVendor == null || stdVendor === '')) {
                return { backgroundColor: '#FFEBEE' };
              }
              return null;
            },
            tooltipValueGetter: (params: any) => {
              const isDiscovered = !!params.data?.__isDiscovered__;
              const stdVendor = params.data?.['std-VendorName'];
              if (isDiscovered && (stdVendor == null || stdVendor === '')) {
                return 'Vendor name needs to be set.';
              }
              return null;
            },
          };
        }
        return col;
      });
      setColumnDefs([expandColumn, vendorTicketColumn, ...enhanced]);
    }
  }, [columnDefs.length, handleExpandRow]);

  // Apply URL filters after columns are initialized (runs once)
  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;
    if (!columnDefsInitialized.current) return;
    if (columnDefs.length === 0) return;

    const nextModel = urlToFilterModel();
    if (Object.keys(nextModel).length === 0) return;

    // Apply filter model and ensure floating filters update
    applyingUrlFiltersRef.current = true;
    api.setFilterModel(nextModel);
    setTimeout(() => {
      api.onFilterChanged();
      applyingUrlFiltersRef.current = false;
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefs.length]); // Run when columnDefs.length changes to apply initial URL filters

  // Reinitialize when org or URL changes
  useEffect(() => {
    // If orgId appears after initial grid ready, ensure datasource is set
    if (orgId && gridApiRef.current && !dataSourceInitialized.current) {
      setupDataSource(gridApiRef.current);
    }
  }, [orgId, setupDataSource]);

  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;
    if (!columnDefsInitialized.current) return; // Wait for columns to be ready
    const nextModel = urlToFilterModel();
    const currentModel = api.getFilterModel?.() || {};
    if (!modelsEqual(currentModel, nextModel)) {
      // Hydrate only when different
      // DON'T purge cache here - let transformedDiscoveredRows effect handle it after data recalculates
      applyingUrlFiltersRef.current = true;
      api.setFilterModel(nextModel);
      // Force floating filters to update their display
      setTimeout(() => {
        api.onFilterChanged();
        applyingUrlFiltersRef.current = false;
      }, 0);
    }
  }, [search, urlToFilterModel, modelsEqual]);

  // Refresh grid when checkbox values change
  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;
    if (!dataSourceInitialized.current) return;
    // Set loading state immediately when checkboxes change
    setLoading(true);
    api.purgeInfiniteCache?.();
  }, [includeMatData, includeDiscovered, vendorExistsFilter]);

  // Refresh grid when search/filters change OR when discovered data loads.
  // Purge is always needed because AG Grid's IRM may call getRows before
  // React state (currentSearchRef) has updated, producing stale results.
  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;
    if (!dataSourceInitialized.current) return;

    // Delay purge slightly to ensure:
    // 1. currentSearchRef has updated via useLayoutEffect after React re-render
    // 2. transformedDiscoveredRows has recalculated with new search params
    const timer = setTimeout(() => {
      api.purgeInfiniteCache?.();
    }, 50);

    return () => clearTimeout(timer);
  }, [search, transformedDiscoveredRows, includeDiscovered]);

  // Workaround: AG Grid doesn't fire onFilterChanged for paste events.
  // Listen for paste on floating filter inputs and fire it manually after
  // AG Grid's internal debounce (300ms) has processed the pasted value.
  useEffect(() => {
    const gridElement = document.getElementById('mat-grid');
    if (!gridElement) return;

    let timeout: NodeJS.Timeout | null = null;

    const handlePaste = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target?.tagName === 'INPUT' && target.closest('.ag-floating-filter')) {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          const api = gridApiRef.current;
          if (!api || applyingUrlFiltersRef.current) return;
          api.onFilterChanged();
        }, 350);
      }
    };

    gridElement.addEventListener('paste', handlePaste, true);
    return () => {
      gridElement.removeEventListener('paste', handlePaste, true);
      if (timeout) clearTimeout(timeout);
    };
  }, [applyingUrlFiltersRef]);

  // Grid callbacks are memoised so a re-render (one per block load, via setLoading)
  // does not hand AG Grid a fresh set of grid options each time.
  const handleGridReady = useCallback(
    (params: any) => {
      gridApiRef.current = params.api;
      // Expose refresh function to parent
      if (onRefreshNeeded) {
        const refreshFn = () => {
          gridApiRef.current?.purgeInfiniteCache?.();
        };
        onRefreshNeeded(refreshFn);
      }
      // Set initial filter model to match URL so inputs reflect state immediately
      const initialModel = urlToFilterModel();
      const currentModel = params.api.getFilterModel?.() || {};
      if (!modelsEqual(currentModel, initialModel)) {
        applyingUrlFiltersRef.current = true;
        params.api.setFilterModel(initialModel);
        setTimeout(() => (applyingUrlFiltersRef.current = false), 0);
      }
      setupDataSource(params.api);
    },
    [onRefreshNeeded, urlToFilterModel, modelsEqual, setupDataSource, applyingUrlFiltersRef]
  );

  const handleFilterChanged = useCallback(() => {
    if (applyingUrlFiltersRef.current) return;
    const model = gridApiRef.current?.getFilterModel?.() || {};
    setUrlFromFilterModel(model);
    // Do not purge here; the search-change effect will purge after React re-renders
  }, [applyingUrlFiltersRef, setUrlFromFilterModel]);

  const handleSortChanged = useCallback(() => {
    gridApiRef.current?.purgeInfiniteCache?.();
  }, []);

  const handleCellContextMenu = useCallback((params: CellContextMenuEvent) => {
    params.event?.preventDefault?.();
    const ev = params.event as MouseEvent | null;
    setMenuState({
      mouseX: ev?.clientX ?? 0,
      mouseY: ev?.clientY ?? 0,
      cellRaw: params?.value,
      columnField: params?.colDef?.field,
    });
  }, []);

  const getRowClass = useCallback(
    (params: GetRowClassParams) =>
      getRowClasses(params, selectedRowDataRef.current?.MatKey, allMatKeys),
    [allMatKeys]
  );

  return (
    <Paper id="mat-grid" sx={{ position: 'relative', overflow: 'visible' }}>
      {/*
        Overlaid rather than placed in flow: every infinite-scroll block toggles this,
        and an in-flow bar shifts the grid down and back on each page fetch.
      */}
      {isPending && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }}>
          <LinearProgress />
        </div>
      )}
      {error && <div style={{ padding: '8px 12px', color: '#b71c1c' }}>{error}</div>}
      <div
        className="ag-theme-balham ag-glynt"
        style={{ height: '78vh', width: '100%', position: 'relative' }}
        data-cy="mat-grid"
        onContextMenu={(e) => {
          // Suppress the browser default context menu within the grid area
          e.preventDefault();
        }}
      >
        <AgGridReact
          ref={gridRef}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          suppressNoRowsOverlay={false}
          rowModelType="infinite"
          cacheBlockSize={MAT_GRID_CACHE_BLOCK_SIZE}
          maxBlocksInCache={MAT_GRID_MAX_BLOCKS_IN_CACHE}
          blockLoadDebounceMillis={MAT_GRID_BLOCK_LOAD_DEBOUNCE_MS}
          maxConcurrentDatasourceRequests={MAT_GRID_MAX_CONCURRENT_REQUESTS}
          rowBuffer={MAT_GRID_ROW_BUFFER}
          animateRows={false}
          pagination={false}
          overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading...</span>'
          enableBrowserTooltips={true}
          enableCellTextSelection={true}
          onGridReady={handleGridReady}
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
          onCellContextMenu={handleCellContextMenu}
          getRowClass={getRowClass}
        />
      </div>
      <MATGridContextMenu
        open={menuState.mouseY !== null}
        position={{ mouseX: menuState.mouseX, mouseY: menuState.mouseY }}
        columnField={menuState.columnField}
        cellRaw={menuState.cellRaw}
        onClose={closeMenu}
        gridApi={gridApiRef.current}
        filterableColumns={SERVER_FILTER_FIELDS}
        onFilter={handleContextMenuFilter}
      />
      <MATRowDetailDrawer
        open={drawerOpen}
        data={selectedRowData}
        onClose={handleCloseDrawer}
        onSuccess={handleDrawerSuccess}
        cFieldKeys={cFieldKeys}
      />
    </Paper>
  );
};

// Every prop the page passes is stable, so memo keeps page-level state changes
// (counts, tour, toasts) from re-rendering the grid.
export default memo(MATGrid);
